import { useQuery } from '@apollo/client';
import { CheckBox, PillGroup, TextBox } from '@homex/se-react-components';
import {
  CarouselItemId,
  FlowCarouselItemId,
} from '@homex/se-widget-flow-types';
import { loader } from 'graphql.macro';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from 'styled-components/macro';

import {
  setContactInfoPhoneInputValue,
  setCurrentCarouselItem,
  setCustomerLocationPhone,
  setCustomerLookupCustomerLocations,
  setCustomerLookupPhoneInputValue,
  setCustomerPhone,
  setIsPreviousCustomer,
  setMobileNotificationOptIn,
} from 'actions';
import { CarouselBottomButton } from 'components/CarouselBottomButton/CarouselBottomButton';
import { Phrase } from 'components/Phrase';
import { MOBILE_NOTIFICATION_DISCLAIMER } from 'components/constants';
import { ReactComponent as InputStandardPhoneSvg } from 'components/svg/InputStandardPhone.svg';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import {
  CarouselReduxReducer,
  CustomerLookupCarouselItem,
  IState,
  ServiceType,
} from 'typings';
import {
  IGetCorrectedPhoneNumberQuery,
  IGetCorrectedPhoneNumberQueryVariables,
  IGetCustomerLocationsByPhoneQuery,
  IGetCustomerLocationsByPhoneQueryVariables,
} from 'typings/GraphQL';
import getPhoneNumberDigits from 'utils/getPhoneNumberDigits';
import isMaskedPhoneInputValid from 'utils/isMaskedPhoneInputValid';
import { tryGetFlowCarouselItem } from 'utils/tryGetFlowCarouselItem';

import {
  Container,
  Description,
  FormGroup,
  Logo,
  OptInContainer,
} from './styles';

enum PreviousCustomerOption {
  NO = 'NO',
  YES = 'YES',
}

const getCorrectedPhoneNumberQuery = loader(
  '../../queries/getCorrectedPhoneNumber.graphql',
);
const getCustomerLocationsByPhoneQuery = loader(
  '../../queries/getCustomerLocationsByPhone.graphql',
);

export const CAROUSEL_ITEM_ID = CarouselItemId.CustomerLookup;

export const CarouselItemCustomerLookupBottom = () => {
  const theme = useTheme();
  const dispatch = useThunkDispatch();
  const {
    countryCode = 'US',
    currentCarouselItemId,
    customerLookupPhoneInputValue,
    erpKey,
    flow,
    isPreviousCustomer,
    mobileNotificationOptIn,
    serviceType = ServiceType.REPAIR,
  } = useSelector((state: IState) => ({
    countryCode: state.client.countryCodes[0],
    currentCarouselItemId: state.serviceCarousel.currentItemId,
    customerLookupPhoneInputValue:
      state.userInput.customerLookupPhoneInputValue,
    erpKey: state.client.erpKey,
    flow: state.client.flow,
    isPreviousCustomer: state.userInput.isPreviousCustomer,
    mobileNotificationOptIn: state.userInput.mobileNotificationOptIn,
    serviceType: state.userInput.serviceType,
  }));

  const flowCarouselItem = tryGetFlowCarouselItem<CustomerLookupCarouselItem>(
    flow,
    CAROUSEL_ITEM_ID,
  );

  const isCurrentCarouselItemVisible =
    currentCarouselItemId === CarouselItemId.CustomerLookup;
  const isPhoneInputValid = isMaskedPhoneInputValid(
    customerLookupPhoneInputValue,
  );

  const {
    data: getCorrectedPhoneNumberData,
    error: getCorrectedPhoneNumberError,
    loading: isGetCorrectedPhoneNumberQueryLoading,
  } = useQuery<
    IGetCorrectedPhoneNumberQuery,
    IGetCorrectedPhoneNumberQueryVariables
  >(getCorrectedPhoneNumberQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const phone = data?.getCorrectedPhoneNumber?.phone;

      if (phone) {
        dispatch(setCustomerPhone(phone));
      }
    },
    skip: !isPhoneInputValid || !isCurrentCarouselItemVisible,
    variables: {
      countryCode,
      phoneNumber: customerLookupPhoneInputValue,
    },
  });

  const correctedPhoneNumber =
    getCorrectedPhoneNumberData?.getCorrectedPhoneNumber?.phone?.number || '';
  const isCorrectedPhoneNumberValid =
    !!getCorrectedPhoneNumberData?.getCorrectedPhoneNumber?.isValid;

  const {
    data: getCustomerLocationsByPhoneData,
    error: getCustomerLocationsByPhoneError,
    loading: isGetCustomerLocationsByPhoneQueryLoading,
  } = useQuery<
    IGetCustomerLocationsByPhoneQuery,
    IGetCustomerLocationsByPhoneQueryVariables
  >(getCustomerLocationsByPhoneQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const allowLocationsNotInServiceZone =
        !!data?.getCustomerLocationsByPhone?.allowLocationsNotInServiceZone;

      const customerLocations =
        data?.getCustomerLocationsByPhone?.customerLocations || [];

      dispatch(
        setCustomerLookupCustomerLocations(
          customerLocations,
          allowLocationsNotInServiceZone,
        ),
      );
    },
    skip: !isCorrectedPhoneNumberValid || !isCurrentCarouselItemVisible,
    variables: {
      phoneNumber: correctedPhoneNumber,
      serviceType,
    },
  });

  const doNotServiceCustomer =
    getCustomerLocationsByPhoneData?.getCustomerLocationsByPhone
      ?.doNotServiceCustomer || false;
  const isLoading =
    isGetCorrectedPhoneNumberQueryLoading ||
    isGetCustomerLocationsByPhoneQueryLoading;

  const customerLocations =
    getCustomerLocationsByPhoneData?.getCustomerLocationsByPhone
      ?.customerLocations || [];

  const hasCustomerLocations = customerLocations.length > 0;
  const isAllowedToBook = customerLocations.every(
    (location) => location.isBookingAllowed,
  );

  const isCustomerFetchSuccess =
    !isLoading && isPhoneInputValid && hasCustomerLocations && isAllowedToBook;

  const isErrorMessageVisible =
    (!isLoading && isPhoneInputValid && !hasCustomerLocations) ||
    getCorrectedPhoneNumberError ||
    getCustomerLocationsByPhoneError;

  const isWarningMessageVisible =
    !isErrorMessageVisible && !isAllowedToBook && !isLoading;

  const handleIsPreviousCustomerChange = (
    event: React.FormEvent<HTMLInputElement>,
  ) => {
    dispatch(
      setIsPreviousCustomer(
        event.currentTarget.value === PreviousCustomerOption.YES,
      ),
    );
  };

  const isPreviousCustomerWithLocations =
    !!isPreviousCustomer && hasCustomerLocations && isPhoneInputValid;

  let destinationItemId: FlowCarouselItemId;
  const bottomButtonFormat = flow ? flow.buttonType : theme.buttonType;

  if (flowCarouselItem) {
    if (isPreviousCustomerWithLocations) {
      destinationItemId =
        flowCarouselItem.options.destinations.previousCustomerWithLocations;
    } else {
      destinationItemId = flowCarouselItem.options.destinations.next;
    }
  } else {
    destinationItemId =
      isPreviousCustomerWithLocations &&
      // If customerAddressSelectionEnabled is undefined, it should be treated
      // as true since this was the previous default behavior.
      theme.customerAddressSelectionEnabled !== false
        ? CarouselItemId.CustomerLocations
        : CarouselItemId.RepairContactInfo;
  }

  const handleContinueClick = () => {
    dispatch(
      setCurrentCarouselItem(CarouselReduxReducer.SERVICE, destinationItemId),
    );
  };

  const handlePhoneChange = (event: React.FormEvent<HTMLInputElement>) => {
    const value = getPhoneNumberDigits(event.currentTarget.value);

    dispatch(setCustomerLookupPhoneInputValue(value));
    dispatch(setContactInfoPhoneInputValue(value));
  };

  const handleOptInChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setMobileNotificationOptIn(event.target.checked));
  };

  const radioPillBoxValue =
    isPreviousCustomer === undefined
      ? undefined
      : isPreviousCustomer
      ? PreviousCustomerOption.YES
      : PreviousCustomerOption.NO;

  const isDisabled =
    !radioPillBoxValue ||
    (isPreviousCustomer && (!hasCustomerLocations || !isCustomerFetchSuccess));

  useEffect(() => {
    // If the customer lookup component mounts, the customer location phone is
    // no longer valid, so we clear it out
    dispatch(setCustomerLocationPhone({}));
  }, [dispatch]);

  const getErrorMessage = () =>
    doNotServiceCustomer ? (
      <Phrase id="CUSTOMER_LOOKUP.DO_NOT_SERVICE_CUSTOMER.MESSAGE" />
    ) : (
      isErrorMessageVisible && <Phrase id="CUSTOMER_LOOKUP.PHONE.ERROR" />
    );

  return (
    <FormGroup>
      <div>
        <Logo />
        <PillGroup
          onChange={handleIsPreviousCustomerChange}
          options={[
            {
              key: PreviousCustomerOption.YES,
              label: Phrase({ id: 'CUSTOMER_LOOKUP.YES.LABEL' }),
              value: PreviousCustomerOption.YES,
            },
            {
              key: PreviousCustomerOption.NO,
              label: Phrase({ id: 'CUSTOMER_LOOKUP.NO.LABEL' }),
              value: PreviousCustomerOption.NO,
            },
          ]}
          title={Phrase({ id: 'CUSTOMER_LOOKUP.LEGEND' })}
          value={radioPillBoxValue}
        />
        {isPreviousCustomer && (
          <Container>
            <TextBox
              autoComplete="tel"
              cleaveOptions={{ phone: true, phoneRegionCode: countryCode }}
              description={<Phrase id="CUSTOMER_LOOKUP.PHONE.DESCRIPTION" />}
              errorMessage={getErrorMessage()}
              isLoading={isLoading}
              isSuccessful={isCustomerFetchSuccess}
              label={Phrase({ id: 'CUSTOMER_LOOKUP.PHONE.LABEL' })}
              leftIcon={InputStandardPhoneSvg}
              name="phone"
              onChange={handlePhoneChange}
              type="tel"
              value={customerLookupPhoneInputValue}
              warningMessage={
                isWarningMessageVisible && (
                  <Phrase id="CUSTOMER_LOOKUP.PHONE.NOBOOK.HTML" />
                )
              }
            />
            {erpKey === 'servicetitan' && (
              <OptInContainer>
                <CheckBox
                  checked={mobileNotificationOptIn}
                  id="phone-opt-in"
                  onChange={handleOptInChange}
                />
                <span className="disclaimer-text">
                  {MOBILE_NOTIFICATION_DISCLAIMER}
                </span>
              </OptInContainer>
            )}
          </Container>
        )}
        {isPreviousCustomer === false && (
          <Description>
            <Phrase id="CUSTOMER_LOOKUP.NO.DESCRIPTION" />
          </Description>
        )}
      </div>
      <CarouselBottomButton
        buttonFormat={bottomButtonFormat}
        carouselItemId={CAROUSEL_ITEM_ID}
        isDisabled={isDisabled}
        onContinueClick={handleContinueClick}
      />
    </FormGroup>
  );
};
